/**
 * `IPermissionsModel` — port of Unity's `Models/PermissionsModel.cs`.
 *
 * "What can the current user do in the application right now." Every predicate
 * here is a verbatim port; the differences between them are the whole point of
 * the file and are load-bearing:
 *
 *   canAuthorProject()   = PRESENTER **and** (Owner|Editor)
 *   canBeAuthor()        = (Owner|Editor) alone            ← one conjunct apart
 *   canChangeVisibility()= PRESENTER alone, no role term
 *   canShareProject()    = (Owner|Editor) alone, no presenter term
 *   canModifyProject()   = PRESENTER **and** Owner
 *   canSelectScene()     = PRESENTER **and** presenter-eligible
 *
 * A porter who collapses any pair of these reproduces roughly none of the
 * desktop gating — `RightPanelViewModel.cs` uses `CanAuthorProject` and
 * `CanBeAuthor` three lines apart (`:573` vs `:579`).
 *
 * ⚠ THE PRESENTER CONJUNCT IS DELIBERATE AND WAS A DIRECTION CALL. On the
 * desktop client an Owner who is not currently the presenter gets no
 * inspector, no gizmos, no import and no scene editing. The web adopts it
 * (full parity), which means a user is briefly read-only on project open
 * until the 1 Hz `ensurePresenter` self-elects them. That self-election is
 * gated on presenter ELIGIBILITY, and a project's own creator is always
 * eligible (`CustomerModel.GetCurrentUserRole` returns `(Owner, true)` for
 * a project you created), so the solo case always heals. A sharee whose
 * account has `canBePresenter = false` genuinely cannot author — exactly as
 * on desktop.
 *
 * LAYERING: this is a headless model. It emits; it never raises a dialog. The
 * "Project permissions have changed" confirmation is driven by the ViewModel
 * layer off {@link onPermissionsChanged} / {@link onAccessRevoked}
 * (`Campfire_Web_Runtime.md:48`).
 */

import type { Emitter } from "@core/events/emitter";
import type { AccountRole, SharedUserRole } from "@models/data/userRolePermissions";
import type { ChangeSharingEvent } from "@models/data/changeSharingWire";

/**
 * Port of `PermissionsEditorMode` (`PermissionsModel.cs:11-18`).
 *
 * ⚠ THREE OF THE FIVE ARE DEAD. `Viewer`, `Commenter` and `Writer` are
 * computed by `updateUIPermissions()` and read by NOTHING — every predicate
 * compares only `=== Presenter` or `=== Rightless`. The enum functionally
 * collapses to three states. Ported whole for fidelity; do NOT build UI that
 * distinguishes the dead three.
 */
export const PERMISSIONS_EDITOR_MODE = {
  Viewer: 0,
  Rightless: 1,
  Presenter: 2,
  Commenter: 3,
  Writer: 4,
} as const;

export type PermissionsEditorMode =
  (typeof PERMISSIONS_EDITOR_MODE)[keyof typeof PERMISSIONS_EDITOR_MODE];

/** How the account-role fetch is doing. A FAILURE is not "Reader". */
export type RoleLoadState = "idle" | "loading" | "loaded" | "failed";

export interface IPermissionsModel {
  // ---- Events --------------------------------------------------------

  /**
   * Raised only on a REAL transition (`PermissionsModel.cs:149-154`).
   *
   * A `computed` that mirrors a predicate on this model has no reactive
   * dependency and freezes on its first answer — no error, no warning
   * (`Campfire_Web_UI.md:102-140`). Bump a version ref from THIS emitter and
   * `void` it at the top of every computed. The role legitimately changes at
   * least three times after first paint (sign-in, project load, an inbound
   * `change_sharing`), so a frozen mirror is the most likely silent failure
   * of the whole permissions port.
   */
  readonly onPermissionsChanged: Emitter<void>;

  /**
   * The active project's sharing was revoked out from under this user
   * (`AccountRoleChanged` carrying `NoAccess`). Unity routes straight to the
   * projects grid with no dialog (`PermissionsModel.cs:250-254`).
   */
  readonly onAccessRevoked: Emitter<{ projectId: string }>;

  /**
   * The user's role on the OPEN project changed while they were in it. The
   * ViewModel layer answers with Unity's confirmation
   * ("Project permissions have changed" / "Refresh").
   */
  readonly onProjectRoleChanged: Emitter<{ projectId: string; role: SharedUserRole }>;

  // ---- State ---------------------------------------------------------

  readonly editorMode: PermissionsEditorMode;
  /** `UserInfo.EffectiveRole` — the per-project account role. */
  readonly effectiveRole: AccountRole;
  /** `UserInfo.NaturalAccountRole` — the licence role from the admin portal. */
  readonly naturalAccountRole: AccountRole;
  /** How the account-role fetch went. `"failed"` must not read as Reader. */
  readonly roleLoadState: RoleLoadState;

  /**
   * `IProjectModel.ActiveUserRole` (`ProjectModelSlim.cs:149-162`): Viewer with
   * no project, Owner when you created it, else the effective account role
   * mapped across. This is the value `ProjectModel.activeUserRole` delegates
   * to, so there is ONE definition.
   */
  readonly activeUserRole: SharedUserRole;

  // ---- Predicates (all verbatim ports) --------------------------------

  updateUIPermissions(): void;

  /** `CanBePresenter()` — `UserInfo.CanBePresenter`, per-project. */
  canBePresenter(): boolean;
  /** `CanUseTool(tool)` — the two-armed table; see `userRolePermissions.ts`. */
  canUseTool(tool: number): boolean;
  /** `CanSelectScene()` — presenter AND presenter-eligible. */
  canSelectScene(): boolean;
  /** `CanAuthorProject()` — presenter AND (Owner|Editor). */
  canAuthorProject(): boolean;
  /**
   * `CanUseGizmos()` — author, OR presenting with the CUTTING PLANE selected
   * (so a presenter may always adjust a slice). Selection-dependent, hence
   * the argument; upstream reaches into `IEditorModel` instead and NREs when
   * there is no local participant (`:324-329`), which we guard.
   */
  canUseGizmos(isCuttingPlaneSelected: boolean): boolean;
  /** `CanChangeVisibility()` — presenter ONLY. No role term. */
  canChangeVisibility(): boolean;
  /** `CanBeAuthor()` — `canAuthorProject` WITHOUT the presenter term. */
  canBeAuthor(): boolean;
  /** `CanShareProject()` — Owner|Editor, no presenter term. */
  canShareProject(): boolean;
  /** `CanModifyProject()` — presenter AND Owner. Delete / rename. */
  canModifyProject(): boolean;
  /** `IsRightless()` — no local participant at all; an error state. */
  isRightless(): boolean;
  /** `CanUsePaidFeatures()` — the ACTIVE PROJECT is on a paid plan. */
  canUsePaidFeatures(): boolean;
  /** `IsEffectiveFreeUser()` — your own project, without a core licence. */
  isEffectiveFreeUser(): boolean;
  /** `HasCoreLicense()` — effective AND natural are both Writer. BOTH terms. */
  hasCoreLicense(): boolean;

  // ---- Per-project role lifecycle (`CustomerModel`) --------------------

  /** `CustomerModel.LoadCurrentUserRole()` — resolve for the open project. */
  loadCurrentUserRoleAsync(signal?: AbortSignal): Promise<void>;
  /**
   * `PermissionsModel.OnServerSharingChanged` — apply an inbound
   * `change_sharing` event for the OPEN project. Carries its own own-echo
   * guard, which no other consumer of that event has (see the wire header).
   */
  applyChangeSharing(evt: ChangeSharingEvent): void;

  /** `CustomerModel.SetUserProjectRole(role)` — an inbound sharing change. */
  setUserProjectRole(role: SharedUserRole): void;
  /**
   * `UserInfo.ResetEffectiveRole()` — back to the natural account role on
   * leaving a project (`ProjectsViewModel.cs:1868`). WITHOUT this a sharee's
   * downgraded role leaks into their own projects.
   */
  resetEffectiveRole(): void;

  dispose(): void;
}

export const PERMISSIONS_MODEL = Symbol.for("Campfire.PermissionsModel");
